import type { WorkspaceServerService } from '@affine/core/modules/cloud/services/workspace-server';
import {
  type DocRole,
  grantDocGroupRoleMutation,
  grantDocUserRolesMutation,
  permanentlyDeleteDocMutation,
  restoreDocMutation,
  revokeDocGroupRoleMutation,
  revokeDocUserRolesMutation,
  trashDocMutation,
  updateDocUserRoleMutation,
} from '@affine/graphql';
import { Service } from '@toeverything/infra';

import type { WorkspaceService } from '../../workspace/services/workspace';
import type { AccessibleDocCacheStore } from '../stores/accessible-doc-cache';
import { prepareDatabaseSystemDocsForServerMutation } from './database-system-doc-access';

export type DocGrantUserRolesPayload = {
  docId: string;
  userIds: string[];
  role: string;
};

export type DocGrantGroupRolesPayload = {
  docId: string;
  groupRoles: Array<{
    groupId: string;
    role: string;
  }>;
};

export type DocUpdateUserRolePayload = {
  docId: string;
  userId: string;
  role: string;
};

export type DocRevokeUserRolePayload = {
  docId: string;
  userId: string;
};

export type DocRevokeGroupRolePayload = {
  docId: string;
  groupId: string;
};

export class DocMutationService extends Service {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly accessibleDocCache: AccessibleDocCacheStore
  ) {
    super();
    this.accessibleDocCache.setPermanentDeleteHandler(docId =>
      this.permanentlyDelete(docId)
    );
  }

  async setTrashState(docId: string, trashed: boolean): Promise<void> {
    await this.ensureDocServerReady(docId);
    await this.requireServer().gql({
      query: trashed ? trashDocMutation : restoreDocMutation,
      variables: {
        workspaceId: this.workspaceService.workspace.id,
        docId,
      },
    });
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  async permanentlyDelete(docId: string): Promise<void> {
    await this.ensureDocServerReady(docId);
    await this.requireServer().gql({
      query: permanentlyDeleteDocMutation,
      variables: {
        workspaceId: this.workspaceService.workspace.id,
        docId,
      },
    });
    this.accessibleDocCache.confirmPermanentDelete(docId);
  }

  async grantUserRoles(payload: DocGrantUserRolesPayload): Promise<void> {
    await this.ensureDocServerReady(payload.docId);
    await this.requireServer().gql({
      query: grantDocUserRolesMutation,
      variables: {
        input: {
          workspaceId: this.workspaceService.workspace.id,
          docId: payload.docId,
          userIds: payload.userIds,
          role: payload.role as DocRole,
        },
      },
    });
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  async grantGroupRole(
    payload: DocGrantGroupRolesPayload['groupRoles'][number] & {
      docId: string;
    }
  ): Promise<void> {
    await this.grantGroupRoles({
      docId: payload.docId,
      groupRoles: [{ groupId: payload.groupId, role: payload.role }],
    });
  }

  async grantGroupRoles(payload: DocGrantGroupRolesPayload): Promise<void> {
    await this.ensureDocServerReady(payload.docId);
    for (const groupRole of payload.groupRoles) {
      await this.requireServer().gql({
        query: grantDocGroupRoleMutation,
        variables: {
          workspaceId: this.workspaceService.workspace.id,
          docId: payload.docId,
          groupId: groupRole.groupId,
          role: groupRole.role as DocRole,
        },
      });
    }
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  async updateUserRole(payload: DocUpdateUserRolePayload): Promise<void> {
    await this.ensureDocServerReady(payload.docId);
    await this.requireServer().gql({
      query: updateDocUserRoleMutation,
      variables: {
        input: {
          workspaceId: this.workspaceService.workspace.id,
          docId: payload.docId,
          userId: payload.userId,
          role: payload.role as DocRole,
        },
      },
    });
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  async revokeUserRole(payload: DocRevokeUserRolePayload): Promise<void> {
    await this.ensureDocServerReady(payload.docId);
    await this.requireServer().gql({
      query: revokeDocUserRolesMutation,
      variables: {
        input: {
          workspaceId: this.workspaceService.workspace.id,
          docId: payload.docId,
          userId: payload.userId,
        },
      },
    });
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  async revokeGroupRole(payload: DocRevokeGroupRolePayload): Promise<void> {
    await this.ensureDocServerReady(payload.docId);
    await this.requireServer().gql({
      query: revokeDocGroupRoleMutation,
      variables: {
        workspaceId: this.workspaceService.workspace.id,
        docId: payload.docId,
        groupId: payload.groupId,
      },
    });
    await this.accessibleDocCache.fetchAccessibleDocs();
  }

  private requireServer() {
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new Error('No server');
    }
    return server;
  }

  private async ensureDocServerReady(docId: string): Promise<void> {
    if (!this.accessibleDocCache.isLocallyCreated(docId)) {
      return;
    }

    await prepareDatabaseSystemDocsForServerMutation({
      collection: this.workspaceService.workspace.docCollection,
      docEngine: this.workspaceService.workspace.engine.doc,
      docId,
      workspaceId: this.workspaceService.workspace.id,
    });

    const confirmed = await this.accessibleDocCache.waitForServerConfirmed(
      docId,
      {
        timeoutMs: 10000,
        pollMs: 250,
      }
    );
    if (!confirmed) {
      throw new Error('Doc server confirmation pending');
    }
  }
}
